import { useState } from 'react';

type Platform = 'facebook' | 'instagram' | 'linkedin' | 'gbp';

interface SocialPreviewProps {
  businessName: string;
  postText: string;
  imageUrl?: string;
  hashtags?: string[];
}

const PLATFORMS: { key: Platform; label: string; icon: string; color: string }[] = [
  { key: 'facebook', label: 'Facebook', icon: 'f', color: '#1877F2' },
  { key: 'instagram', label: 'Instagram', icon: '📷', color: '#E4405F' },
  { key: 'linkedin', label: 'LinkedIn', icon: 'in', color: '#0A66C2' },
  { key: 'gbp', label: 'Google', icon: 'G', color: '#4285F4' },
];

export default function SocialPreview({ businessName, postText, imageUrl, hashtags }: SocialPreviewProps) {
  const [activePlatform, setActivePlatform] = useState<Platform>('facebook');

  return (
    <div className="space-y-3">
      {/* Platform tabs */}
      <div className="flex gap-1 bg-charcoal-50 rounded-lg p-1">
        {PLATFORMS.map((p) => (
          <button
            key={p.key}
            onClick={() => setActivePlatform(p.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
              activePlatform === p.key
                ? 'bg-white shadow-sm text-charcoal-700'
                : 'text-charcoal-400 hover:text-charcoal-600'
            }`}
          >
            <span
              className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
              style={{ backgroundColor: p.color }}
            >
              {p.icon}
            </span>
            <span className="hidden sm:inline">{p.label}</span>
          </button>
        ))}
      </div>

      {/* Preview card */}
      <div className="border border-charcoal-200 rounded-xl overflow-hidden bg-white shadow-sm max-w-md mx-auto">
        {activePlatform === 'facebook' && (
          <FacebookPreview businessName={businessName} postText={postText} imageUrl={imageUrl} />
        )}
        {activePlatform === 'instagram' && (
          <InstagramPreview businessName={businessName} postText={postText} imageUrl={imageUrl} hashtags={hashtags} />
        )}
        {activePlatform === 'linkedin' && (
          <LinkedInPreview businessName={businessName} postText={postText} imageUrl={imageUrl} />
        )}
        {activePlatform === 'gbp' && (
          <GBPPreview businessName={businessName} postText={postText} imageUrl={imageUrl} />
        )}
      </div>
    </div>
  );
}

/* ═══════════ FACEBOOK ═══════════ */
function FacebookPreview({ businessName, postText, imageUrl }: { businessName: string; postText: string; imageUrl?: string }) {
  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
          {businessName.charAt(0)}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{businessName}</p>
          <p className="text-xs text-gray-500">Just now · 🌐</p>
        </div>
      </div>
      {/* Post body */}
      <div className="px-3 pb-2">
        <p className="text-sm text-gray-800 whitespace-pre-line line-clamp-5">{postText}</p>
      </div>
      {/* Image */}
      {imageUrl && (
        <div className="bg-gray-100 aspect-video flex items-center justify-center">
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      {!imageUrl && (
        <div className="bg-gradient-to-br from-copper-100 to-cream-100 aspect-video flex items-center justify-center">
          <span className="text-copper-400 text-sm">Post image</span>
        </div>
      )}
      {/* Reactions bar */}
      <div className="px-3 py-2 flex items-center justify-between border-t border-gray-100">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <span className="inline-flex -space-x-1">
            <span className="w-4 h-4 rounded-full bg-blue-500 border border-white inline-block text-[8px] text-center text-white leading-4">👍</span>
            <span className="w-4 h-4 rounded-full bg-red-500 border border-white inline-block text-[8px] text-center text-white leading-4">❤️</span>
          </span>
          <span>24</span>
        </div>
        <span className="text-xs text-gray-500">3 comments · 1 share</span>
      </div>
      {/* Action buttons */}
      <div className="flex border-t border-gray-100">
        {['👍 Like', '💬 Comment', '↗️ Share'].map(action => (
          <button key={action} className="flex-1 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50 transition">
            {action}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ═══════════ INSTAGRAM ═══════════ */
function InstagramPreview({ businessName, postText, imageUrl, hashtags }: { businessName: string; postText: string; imageUrl?: string; hashtags?: string[] }) {
  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-[2px]">
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-xs font-bold">
            {businessName.charAt(0)}
          </div>
        </div>
        <p className="text-sm font-semibold text-gray-900">{businessName.toLowerCase().replace(/\s+/g, '')}</p>
        <span className="ml-auto text-gray-400">•••</span>
      </div>
      {/* Image */}
      {imageUrl ? (
        <div className="bg-gray-100 aspect-square">
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="bg-gradient-to-br from-purple-100 via-pink-50 to-orange-100 aspect-square flex items-center justify-center">
          <span className="text-pink-400 text-sm">Post image</span>
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center gap-4 px-3 py-2">
        <span className="text-lg">♡</span>
        <span className="text-lg">💬</span>
        <span className="text-lg">↗️</span>
        <span className="ml-auto text-lg">🔖</span>
      </div>
      {/* Likes */}
      <div className="px-3">
        <p className="text-xs font-semibold text-gray-900">142 likes</p>
      </div>
      {/* Caption */}
      <div className="px-3 pb-3">
        <p className="text-xs text-gray-800">
          <span className="font-semibold">{businessName.toLowerCase().replace(/\s+/g, '')} </span>
          <span className="line-clamp-3">{postText}</span>
        </p>
        {hashtags && hashtags.length > 0 && (
          <p className="text-xs text-blue-900 mt-1">
            {hashtags.map(h => `#${h.replace(/^#/, '')}`).join(' ')}
          </p>
        )}
      </div>
    </div>
  );
}

/* ═══════════ LINKEDIN ═══════════ */
function LinkedInPreview({ businessName, postText, imageUrl }: { businessName: string; postText: string; imageUrl?: string }) {
  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white font-bold text-lg">
          {businessName.charAt(0)}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{businessName}</p>
          <p className="text-xs text-gray-500">Healthcare Practice · Just now</p>
          <p className="text-xs text-gray-500">🌐</p>
        </div>
      </div>
      {/* Body */}
      <div className="px-3 pb-2">
        <p className="text-sm text-gray-800 whitespace-pre-line line-clamp-4">{postText}</p>
        {postText.length > 200 && <span className="text-xs text-gray-500 cursor-pointer">...see more</span>}
      </div>
      {/* Image */}
      {imageUrl ? (
        <img src={imageUrl} alt="" className="w-full aspect-video object-cover" />
      ) : (
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 aspect-video flex items-center justify-center">
          <span className="text-blue-400 text-sm">Post image</span>
        </div>
      )}
      {/* Engagement */}
      <div className="px-3 py-2 text-xs text-gray-500 flex justify-between border-t border-gray-100">
        <span>👍 18 · 💡 4</span>
        <span>5 comments</span>
      </div>
      {/* Actions */}
      <div className="flex border-t border-gray-100">
        {['👍 Like', '💬 Comment', '↗️ Repost', '📤 Send'].map(action => (
          <button key={action} className="flex-1 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition">
            {action}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ═══════════ GOOGLE BUSINESS PROFILE ═══════════ */
function GBPPreview({ businessName, postText, imageUrl }: { businessName: string; postText: string; imageUrl?: string }) {
  return (
    <div>
      {/* Google card header */}
      <div className="bg-white p-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">G</div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{businessName}</p>
            <p className="text-xs text-gray-500">Google Business Post</p>
          </div>
        </div>
      </div>
      {/* Image */}
      {imageUrl ? (
        <img src={imageUrl} alt="" className="w-full aspect-video object-cover" />
      ) : (
        <div className="bg-gradient-to-br from-blue-50 to-green-50 aspect-video flex items-center justify-center">
          <span className="text-blue-400 text-sm">Post image</span>
        </div>
      )}
      {/* Body */}
      <div className="p-3">
        <p className="text-sm text-gray-800 whitespace-pre-line line-clamp-4">{postText}</p>
      </div>
      {/* CTA */}
      <div className="px-3 pb-3">
        <button className="px-4 py-2 rounded-full border-2 border-blue-600 text-blue-600 text-xs font-semibold hover:bg-blue-50 transition">
          Learn more
        </button>
      </div>
    </div>
  );
}
